import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';

function GLBChessPiece({ y, hop }) {
  const group = useRef();
  // Load the GLB model (adjust path if needed)
  const { scene } = useGLTF('/rocket.glb');

  useFrame(() => {
    if (group.current) {
      group.current.position.y = y + hop;
      group.current.rotation.y += 0.01;
    }
  });

  return (
    <group ref={group} scale={[1.25, 1.25, 1.25]} position={[0, 0, 0]} rotation={[0, Math.PI, 0]}>
      <primitive object={scene} />
    </group>
  );
}

// Optionally preload the model for performance
useGLTF.preload('/rocket.glb');

export default function ChessPiece3DThree({ scrollY, timelineStartY, timelineEndY, onRocketPositionChange }) {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate timeline progress (0 to 1)
  const progress = Math.max(0, Math.min(1, (scrollY - timelineStartY) / (timelineEndY - timelineStartY)));
  
  // Memoize responsive dimensions to prevent recalculation on every render
  const dimensions = React.useMemo(() => {
    if (windowSize.width <= 480) {
      // Mobile - smaller rocket and positioning, left-aligned
      return {
        width: 80,
        height: 120,
        arrowOffset: 20,
        hopMultiplier: 20,
        cameraPosition: [0, 1.5, 4],
        cameraFov: 35,
        leftPosition: '2rem' // Position on left for mobile timeline
      };
    } else if (windowSize.width <= 768) {
      // Tablet - medium rocket and positioning, left-aligned
      return {
        width: 100,
        height: 150,
        arrowOffset: 25,
        hopMultiplier: 25,
        cameraPosition: [0, 1.8, 4.5],
        cameraFov: 38,
        leftPosition: '2rem' // Position on left for tablet timeline
      };
    } else {
      // Desktop - full size, centered
      return {
        width: 120,
        height: 180,
        arrowOffset: 30,
        hopMultiplier: 30,
        cameraPosition: [0, 2, 5],
        cameraFov: 40,
        leftPosition: '50%' // Centered for desktop
      };
    }
  }, [windowSize.width]); // Only recalculate when window width changes
  
  // State for arrow position
  const [arrowY, setArrowY] = useState(window.innerHeight * 0.9);

  // Update arrow position in useEffect
  useEffect(() => {
    const scrollArrow = document.querySelector('.scroll-indicator');
    if (scrollArrow) {
      const arrowRect = scrollArrow.getBoundingClientRect();
      setArrowY(arrowRect.top + arrowRect.height / 2); // Center of the arrow
    }
  }, [scrollY]); // Re-run when scroll position changes
  
  // Use memoized dimensions
  const { width, height, arrowOffset, hopMultiplier, cameraPosition, cameraFov, leftPosition } = dimensions;
  
  // Define positions for the rocket with responsive adjustments
  const middlePosition = window.innerHeight * 0.5; // Middle of screen
  const endPosition = window.innerHeight * 0.7; // Final end position
  
  // Calculate Y position with smooth transition
  let yPx;
  const arrowWithOffset = arrowY + arrowOffset;
  
  if (arrowWithOffset > middlePosition) {
    // Follow the scroll arrow until it approaches middle
    yPx = arrowWithOffset;
  } else {
    // Smoothly transition to middle and stay there
    const transitionProgress = Math.min(1, (middlePosition - arrowWithOffset) / 50); // Smooth transition over 50px
    yPx = arrowWithOffset + (middlePosition - arrowWithOffset) * transitionProgress;
    
    // After transition, stay in middle until end
    if (progress > 0.8) {
      const lateProgress = (progress - 0.8) / 0.2;
      yPx = middlePosition + (endPosition - middlePosition) * lateProgress;
    }
  }

  // Notify parent component of rocket position for highlighting
  if (onRocketPositionChange) {
    onRocketPositionChange(yPx);
  }

  // Hopping animation with responsive multiplier
  const hop = Math.abs(Math.sin(Date.now() / 350)) * hopMultiplier;

  return (
    <div
      style={{
        position: 'fixed',
        left: leftPosition,
        top: `${yPx - hop}px`,
        width: width,
        height: height,
        pointerEvents: 'none',
        transform: 'translate(-50%, 0%)',
        zIndex: 1000,
      }}
    >
      <Canvas
        camera={{ position: cameraPosition, fov: cameraFov }}
        style={{ background: 'transparent' }}
        gl={{ alpha: true }}
      >
        <ambientLight intensity={0.7} />
        <directionalLight position={[2, 5, 5]} intensity={1.2} />
        <GLBChessPiece y={0} hop={hop / 100} />
      </Canvas>
    </div>
  );
}
